import styled from "@emotion/styled";
import {
    AccountCircleOutlined,
    BusinessCenterOutlined,
    Home,
    Info,
    People,
    PowerSettingsNew,
    Settings,
    ShopOutlined,
    Store,
    Storefront,
    ViewList,
    WbAutoOutlined
} from "@mui/icons-material";
import { MenuList, type SvgIconTypeMap, type Theme } from "@mui/material";
import type { OverridableComponent } from "@mui/material/OverridableComponent";
import clsx from "clsx";
import { get } from "es-toolkit/compat";
import { type ReactNode, useContext, useState } from "react";
import { MenuItemLink, useLogout, useResourceDefinitions, useSidebarState } from "react-admin";
import { groupBy } from "../array";
import { AccountContext, useWebsite } from "../hooks";
import { SocialAuthButton } from "../pages/socialAuthButton";
import { MenuGroup } from "./menu-group";

export const AdminMenu = ({ dense, hasDashboard, className, ...rest }: MenuProps): JSX.Element => {
    const ctx = useContext(AccountContext);
    const logout = useLogout();
    const resourcesDefinitions = useResourceDefinitions();
    const resources = Object.keys(resourcesDefinitions).map(name => resourcesDefinitions[name]).filter(x => {
        if(Array.isArray(x.options?.role)) {
            return x.options.role.includes(ctx?.account.role);
        }
        if(x.options?.kind === "Account") {
            return ctx?.account?.id !== 0;
        }
        return true;
    });
    const [open, setOpen] = useState("");
    const [sidebarOpen] = useSidebarState();
    const grouped = groupBy(resources, x => x.options?.kind);
    const { accountId } = useWebsite();
    return (
        <Root
            className={clsx(
                MenuClasses.main,
                {
                    [MenuClasses.open]: sidebarOpen,
                    [MenuClasses.closed]: !sidebarOpen,
                },
                className
            )}
            {...rest}
        >
            <MenuList {...rest} sx={{ minWidth: 40, padding: 0 }}>
                <MenuItemLink
                    to={"/"}
                    primaryText="Products"
                    leftIcon={<Home color="primary" />}
                    dense={dense}
                    sx={() => ({ textTransform: "capitalize", height: 48 })}
                />
                <MenuItemLink
                    to={`/store`}
                    primaryText="Stores"
                    leftIcon={<Storefront color="primary" />}
                    dense={dense}
                    sx={() => ({ textTransform: "capitalize", height: 48 })}
                />
                <MenuItemLink
                    to={`/about/${accountId}`}
                    primaryText="About"
                    leftIcon={<Info color="primary" />}
                    dense={dense}
                    sx={() => ({ textTransform: "capitalize", height: 48 })}
                />
                {!ctx?.account?.id ? <SocialAuthButton provider="google"/> : <></> }

                {Array.from(grouped.entries())
                    .filter(([group]) => !!group)
                    .map(([group, resources]) => {
                        const Icon = groupIcon(group);
                        return (
                            <MenuGroup
                                {...rest}
                                indicator
                                icon={<Icon sx={t => ({ color: open === group ? t.palette.secondary.main : t.palette.text.primary })} />}
                                name={group}
                                key={group}
                                open={open === group}
                                onClick={() => (open !== group ? setOpen(group) : setOpen(""))}
                            >
                                {resources.map(resource => {
                                    return (
                                        <MenuItemLink
                                            key={resource.name}
                                            to={`/${resource.name}${resource.options.id ? `/${resource.options.id}` : ""}`}
                                            primaryText={resource.options?.label ?? resource.name}
                                            leftIcon={resource.icon ? <resource.icon color="primary" sx={{ marginRight: 2 }} /> : <ViewList />}
                                            dense={dense}
                                            sx={() => ({ textTransform: "capitalize" })}
                                        />
                                    );
                                })}
                                {group === "Account" && (
                                    <MenuItemLink
                                        to={`#`}
                                        primaryText="Logout"
                                        leftIcon={<PowerSettingsNew color="primary" sx={{ marginRight: 2 }} />}
                                        dense={dense}
                                        sx={() => ({ textTransform: "capitalize" })}
                                        onClick={() => {
                                            logout({  }, "/");
                                            ctx?.setAccount({ id: 0, role: "None" });
                                        }}
                                    />
                                )}
                            </MenuGroup>
                        );
                    })}
            </MenuList>
        </Root>
    );
};

const groupIcon = (name: string): OverridableComponent<SvgIconTypeMap<unknown, "svg">> => {
    switch (name) {
        case "Retail":
            return Store;
        case "Inventory":
            return BusinessCenterOutlined;
        case "Settings":
            return Settings;
        case "People":
            return People;
        case "Account":
            return AccountCircleOutlined;
        case "Admin":
            return WbAutoOutlined;
        case "Business":
            return ShopOutlined;
        default:
            return ViewList;
    }
};

export interface MenuProps {
    children?: ReactNode;
    className?: string;
    dense?: boolean;
    hasDashboard?: boolean;
}

const PREFIX = "RaMenu";

const MenuClasses = {
    main: `${PREFIX}-main`,
    open: `${PREFIX}-open`,
    closed: `${PREFIX}-closed`,
};
const div = styled("div");

const Root = div(({ theme }) => {
    const { breakpoints, transitions } = theme as Theme;
    return {
        [`& .${MenuClasses.main}`]: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            marginTop: "0.5em",
            marginBottom: "1em",
            [breakpoints.only("xs")]: {
                marginTop: 0,
            },
            transition: transitions.create("width", {
                easing: transitions.easing.sharp,
                duration: transitions.duration.leavingScreen,
            }),
        },

        [`& .${MenuClasses.open}`]: {
            width: get(theme, "menu.width", MENU_WIDTH),
        },

        [`& .${MenuClasses.closed}`]: {
            width: get(theme, "menu.closedWidth", CLOSED_MENU_WIDTH),
        },
    };
});

const MENU_WIDTH = 240;
const CLOSED_MENU_WIDTH = 55;
